// AdministrativeAreaPeek — the administrativeAreas context surface + a light one-level NAVIGATOR
// (ADR-036), rendered by PeekHost for BOTH peek targets in the tree:
//
//   • a NODE     (`?peek=administrative-areas:<id>`)   — a administrativeArea: kind · name · status,
//     a clickable breadcrumb (country → ancestors, replace-never-stack), the direct
//     CHILDREN as a drill-down list; the acts from the area's ONE registry (`useAreaActions`):
//     Add inside (the main button) · Rename (the one edit) · the door — Move · Reorder
//     (the areas inside) · Disable / Reactivate · Delete (only while nothing uses the area;
//     the peek then steps up to where it sat).
//   • a COUNTRY  (`?peek=geo-country:<iso>`)   — the top of the tree (NOT a administrativeArea:
//     no id, no lifecycle): its top-level administrativeAreas as the same drill-down list, plus
//     "Create administrative area" pre-seeded to it and Reorder (its roots). No Move/Rename/Disable.
//
// ONE component (and ONE ContextPanel) serves both so navigating country↔node keeps the
// SAME face, exactly like node↔node (the sliding sheet around it is PeekHost's — no step
// replays its entrance). No detail endpoint: the target is read from the
// cached administrativeAreas list. All acts gated reference_data:manage; the registry's hook owns the drawers.

import { useTranslation } from 'react-i18next';
import type { AdministrativeAreaNode } from '@ems-app/schemas/administrative-areas/responses';
import { useAdministrativeAreas } from '../../../hooks/useAdministrativeAreas.js';
import { useEntityPeek } from '../../../hooks/useEntityPeek.js';
import { ContextPanel } from '../../../components/patterns/ContextPanel.js';
import { DetailSection } from '../../../components/patterns/DetailSection.js';
import { Breadcrumb, type BreadcrumbItem } from '../../../components/ui/Breadcrumb.js';
import { Badge } from '../../../components/ui/Badge.js';
import { Alert } from '../../../components/ui/Alert.js';
import { PageLoader } from '../../../components/ui/PageLoader.js';
import { icons } from '../../../components/ui/icons.js';
import { countryName, formatNumber } from '../../../lib/format.js';
import { breadcrumbTrail, childrenOf } from './administrativeAreaTree.js';
import { useAreaActions } from './AreaActions.js';

/** What the peek is pointed at — a administrativeArea node, or a country (the tree's top level). */
export type AdministrativeAreaPeekTarget = { kind: 'node'; id: number } | { kind: 'country'; iso: string };

export interface AdministrativeAreaPeekProps {
  projectId: string;
  target: AdministrativeAreaPeekTarget;
  onClose: () => void;
}

export function AdministrativeAreaPeek({ projectId, target, onClose }: AdministrativeAreaPeekProps) {
  const { t, i18n } = useTranslation('administrative-areas');
  const lang = i18n.language;
  const geos = useAdministrativeAreas(projectId);

  const isCountry = target.kind === 'country';
  const loadingTitle = isCountry ? countryName(target.iso, lang) : t('list.title');
  if (geos.isPending) {
    return (
      <ContextPanel host="peek" onClose={onClose} title={loadingTitle}>
        <div className="flex min-h-[8rem] items-center justify-center">
          <PageLoader />
        </div>
      </ContextPanel>
    );
  }
  const all = geos.data ?? [];
  const node = isCountry ? null : (all.find((n) => n.id === target.id) ?? null);
  // A country ISO is always resolvable (Intl display name); a node must exist in the tree.
  if (geos.isError || (!isCountry && !node)) {
    return (
      <ContextPanel host="peek" onClose={onClose} title={loadingTitle}>
        <Alert variant="danger">{t('detail.error')}</Alert>
      </ContextPanel>
    );
  }

  return <AreaPeekBody projectId={projectId} node={node} iso={isCountry ? target.iso : node!.country_iso_code} all={all} onClose={onClose} />;
}

// The loaded peek — its own component so the acts hook runs on a target that is there. The SAME element at the same
// place for an area and a country, so a country↔area walk keeps the one face.
function AreaPeekBody({ projectId, node, iso, all, onClose }: { projectId: string; node: AdministrativeAreaNode | null; iso: string; all: AdministrativeAreaNode[]; onClose: () => void }) {
  const { t, i18n } = useTranslation('administrative-areas');
  const lang = i18n.language;
  const { openPeek } = useEntityPeek();
  const isCountry = node === null;
  const disabled = node?.is_disabled ?? false;
  // The drill-down list: a node's direct children, or a country's top-level roots.
  const listNodes = isCountry
    ? childrenOf(all, null).filter((n) => n.country_iso_code === iso)
    : childrenOf(all, node.id);
  const { acts, overlays } = useAreaActions(projectId, { node, iso, all, inside: listNodes });

  // Breadcrumb (node only): country (→ country peek) → each ancestor (→ its peek,
  // replace-never-stack) → the current node (plain / aria-current, the last item).
  const crumbs: BreadcrumbItem[] | null = node
    ? [
        { label: countryName(iso, lang), onAction: () => openPeek(`geo-country:${iso}`) },
        ...breadcrumbTrail(all, node.id).map(
          (n, i, arr): BreadcrumbItem =>
            i === arr.length - 1 ? { label: n.name } : { label: n.name, onAction: () => openPeek(`administrative-areas:${n.id}`) },
        ),
      ]
    : null;

  return (
    <>
      <ContextPanel
        host="peek"
        onClose={onClose}
        leading={<icons.administrativeArea size={22} aria-hidden className="text-[var(--color-text-secondary)]" />}
        kind={isCountry ? t('country.kind') : t('detail.kind')}
        title={isCountry ? countryName(iso, lang) : node.name}
        status={
          node ? (
            <Badge variant={disabled ? 'neutral' : 'success'} appearance="subtle">
              {disabled ? t('status.disabled') : t('status.active')}
            </Badge>
          ) : undefined
        }
        acts={acts}
        pageless
      >
        {crumbs && <Breadcrumb items={crumbs} />}
        {/* The drill-down list in the boxed section language (P uplift) — the
            count rides the section's action slot, like the sibling peeks. */}
        <DetailSection
          dense
          title={t('peek.inside')}
          action={<span className="text-sm text-[var(--color-text-secondary)] tabular-nums">{formatNumber(listNodes.length)}</span>}
        >
          {listNodes.length === 0 ? (
            <p className="text-sm text-[var(--color-text-secondary)]">{isCountry ? t('country.empty') : t('peek.noChildren')}</p>
          ) : (
            <ul className="flex flex-col gap-0.5">
              {listNodes.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => openPeek(`administrative-areas:${c.id}`)}
                    aria-label={t('peek.open', { name: c.name })}
                    className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-start text-sm text-[var(--color-text-primary)] hover:bg-[var(--color-bg-hover)] outline-none focus-ring-inset"
                  >
                    <icons.administrativeArea aria-hidden size={16} className="shrink-0 text-[var(--color-text-secondary)]" />
                    <span className="flex-1 truncate">{c.name}</span>
                    {c.is_disabled && (
                      <Badge variant="neutral" appearance="subtle">
                        {t('status.disabled')}
                      </Badge>
                    )}
                    <icons.caretRight aria-hidden size={14} className="shrink-0 text-[var(--color-text-disabled)] rtl:-scale-x-100" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </DetailSection>
      </ContextPanel>

      {overlays}
    </>
  );
}
